"use client";

/**
 * Landing Page - Mobile Menu Component
 * Animated slide-in menu for mobile navigation
 */

import { useEffect, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { X } from "lucide-react";
import { Button } from "@/shared/presentation/components/ui/button";
import { siteConfig } from "@/config/site";
import { TIMINGS } from "@/constants";
import { NAV_LINKS, LANDING_IMAGES, LANDING_ROUTES } from "../../constants";
import { ThemeSwitcher } from "@/shared";

interface MobileMenuProps {
  onClose: () => void;
}

export function MobileMenu({ onClose }: MobileMenuProps) {
  const t = useTranslations();
  const [isVisible, setIsVisible] = useState(false);

  // Trigger animation on mount
  useEffect(() => {
    // Small delay to ensure the component is mounted before animating
    const timer = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);

  // Handle close with animation
  const handleClose = () => {
    setIsVisible(false);
    // Wait for animation to complete before actually closing
    setTimeout(onClose, TIMINGS.MENU_TRANSITION);
  };

  return (
    <div
      className={`fixed inset-0 z-50 h-screen w-screen transition-colors duration-300 ${
        isVisible ? "bg-muted/50" : "bg-transparent"
      }`}
      onClick={handleClose}
    >
      <div
        className={`absolute right-0 top-0 z-50 h-screen w-screen max-w-full bg-background p-4 shadow-xl transition-transform duration-300 ease-out sm:w-80 ${
          isVisible ? "translate-x-0" : "translate-x-full"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col items-stretch gap-4">
          {/* Header */}
          <div className="flex justify-between items-center">
            <div className="flex cursor-default items-center justify-center gap-2 text-2xl font-bold uppercase text-muted-foreground">
              <Image
                src={LANDING_IMAGES.logo}
                alt={`${siteConfig.name} logo`}
                width={40}
                height={40}
                className="h-10 w-10"
              />
              <span className="text-xl font-bold uppercase text-secondary">
                {siteConfig.name}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <ThemeSwitcher />
              <Button
                variant="ghost"
                size="icon"
                onClick={handleClose}
                className="rounded px-4 py-2 font-bold text-muted-foreground transition-all hover:bg-secondary hover:bg-opacity-90 hover:text-white"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
          </div>

          {/* Divider */}
          <div className="h-0.5 w-full bg-primary" />

          {/* Navigation Links */}
          {NAV_LINKS.map((link) => (
            <a
              key={link.id}
              href={link.href}
              onClick={handleClose}
              className="my-auto flex items-center justify-center rounded bg-primary px-2 py-2 text-base font-bold capitalize tracking-[1px] text-white transition-all hover:opacity-90"
            >
              {t(link.labelKey)}
            </a>
          ))}

          {/* Dashboard Link */}
          <Link
            href={LANDING_ROUTES.dashboard}
            onClick={handleClose}
            className="my-auto flex items-center justify-center rounded bg-secondary px-2 py-2 text-base font-bold capitalize tracking-[1px] text-white transition-all hover:opacity-90"
          >
            {t("common.dashboard")}
          </Link>
        </div>
      </div>
    </div>
  );
}

export default MobileMenu;
